import React from "react";
import { Progress, Button } from "antd";
import { CloseOutlined, LoadingOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useLanguage } from "../../contexts/LanguageContext";
import FolderCard from "../FolderCard/FolderCard";
import styles from "./UploadProgressCard.module.scss";
import itemStyles from "../ProjectItemCard/ProjectItemCard.module.scss";

export type UploadItemStatus = "pending" | "uploading" | "done" | "error";

export interface UploadProgressItem {
  id: string;
  name: string;
  relativePath: string;
  status: UploadItemStatus;
  error?: string;
  /** 0-100, yükleme sırasında 0→85, başarıda 100 */
  progress?: number;
}

interface UploadProgressCardProps {
  items: UploadProgressItem[];
  onCancel?: () => void;
  isActive: boolean;
  onClose?: () => void;
  isComplete?: boolean;
  /** Grid içinde kartların solunda göster (circular progress) */
  inline?: boolean;
  /** 'entry' = file card görünümü, 'folder' = folder card görünümü */
  variant?: "entry" | "folder";
  /** internal/external tema (FolderCard ile aynı) */
  cardVariant?: "internal" | "external";
  /** Özel başlık (örn. "Duplicating") */
  title?: string;
}

const UploadProgressCard: React.FC<UploadProgressCardProps> = ({
  items,
  onCancel,
  isActive,
  onClose,
  isComplete = false,
  inline = true,
  variant = "entry",
  cardVariant = "internal",
  title: customTitle,
}) => {
  const { t } = useLanguage();
  const doneCount = items.filter((i) => i.status === "done").length;
  const errorCount = items.filter((i) => i.status === "error").length;
  const uploadingCount = items.filter((i) => i.status === "uploading").length;
  const pendingCount = items.filter((i) => i.status === "pending").length;
  const total = items.length;
  const sumProgress = items.reduce(
    (s, i) => s + (i.progress ?? (i.status === "done" || i.status === "error" ? 100 : 0)),
    0
  );
  const percent = total > 0 ? Math.round(sumProgress / total) : 0;

  if (!isActive && items.length === 0) return null;

  if (inline) {
    const title =
      customTitle ??
      (variant === "folder"
        ? t("uploadProgress.untitledFolder")
        : t("uploadProgress.untitledEntry"));
    const fileLabel = total === 1 ? t("uploadProgress.oneFile") : `${total} ${t("uploadProgress.files")}`;
    const dateStr = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
    const isFileType = variant === "entry";

    return (
      <div className={styles.inlineCardWrap}>
        {(uploadingCount > 0 || pendingCount > 0) && onCancel && (
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={(e) => { e.stopPropagation(); onCancel(); }}
            className={styles.inlineCloseBtn}
            aria-label={t("uploadProgress.cancel")}
          />
        )}
        {isComplete && onClose && (
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className={styles.inlineCloseBtn}
            aria-label={t("common.close")}
          />
        )}
        <FolderCard
          folderType={isFileType ? "file" : "folder"}
          variant={cardVariant}
          selectable={false}
        >
          <div className={`${itemStyles.projectImageWrap} ${isFileType ? itemStyles.projectImageWrapFile : itemStyles.projectImageWrapFolder}`}>
            <div className={`${itemStyles.projectImage} ${cardVariant === "external" ? itemStyles.projectImageExternal : itemStyles.projectImageInternal}`}>
              <Progress
                type="circle"
                percent={percent}
                size={80}
                strokeColor="var(--app-accent-color, #00d07d)"
                trailColor="rgba(255,255,255,0.15)"
                format={() => `%${percent}`}
                className={styles.circularProgress}
              />
            </div>
          </div>
          <div className={itemStyles.cardContentBelow}>
            <div className={itemStyles.cardContentRow}>
              <div className={itemStyles.cardContentLeft}>
                <div className={itemStyles.titleText}>{title}</div>
                <div className={itemStyles.dateText}>{fileLabel} · {dateStr}</div>
              </div>
              <div className={itemStyles.cardContentRight} />
            </div>
          </div>
        </FolderCard>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.title}>
          {uploadingCount > 0 || pendingCount > 0
            ? t("uploadProgress.uploading")
            : t("uploadProgress.complete")}
        </span>
        {(uploadingCount > 0 || pendingCount > 0) && onCancel && (
          <Button type="text" size="small" onClick={onCancel} className={styles.cancelBtn}>
            {t("uploadProgress.cancel")}
          </Button>
        )}
        {isComplete && onClose && (
          <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} className={styles.closeBtn} aria-label={t("common.close")} />
        )}
      </div>
      <Progress percent={percent} size="small" showInfo={true} className={styles.progress} />
      <div className={styles.summary}>
        {doneCount > 0 && <span className={styles.done}>{doneCount} {t("uploadProgress.done")}</span>}
        {errorCount > 0 && <span className={styles.error}>{errorCount} {t("uploadProgress.error")}</span>}
      </div>
      <ul className={styles.list}>
        {items.map((item) => (
          <li key={item.id} className={styles.listItem}>
            <span className={styles.itemIcon}>
              {item.status === "pending" && <span className={styles.pendingDot} />}
              {item.status === "uploading" && <LoadingOutlined spin />}
              {item.status === "done" && <CheckCircleOutlined className={styles.iconDone} />}
              {item.status === "error" && <ExclamationCircleOutlined className={styles.iconError} />}
            </span>
            <span className={styles.itemName} title={item.relativePath}>{item.name}</span>
            {item.relativePath !== item.name && (
              <span className={styles.itemPath} title={item.relativePath}>{item.relativePath}</span>
            )}
            {item.status === "error" && item.error && (
              <span className={styles.itemError}>{item.error}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default UploadProgressCard;
